import React, { useEffect, useState } from 'react';
import {
  UserOutlined,
  ShopOutlined,
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
  BulbOutlined,
  BulbFilled,
  BellOutlined,
  DashboardOutlined,
  HighlightOutlined,
  ShoppingOutlined,
  TeamOutlined,
  FileOutlined,
  PieChartOutlined,
  BarChartOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  BgColorsOutlined,
  FontColorsOutlined,
  CloseOutlined,
  CheckOutlined,
  PictureOutlined,
  MenuOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  Layout,
  Menu,
  Dropdown,
  Space,
  Modal,
  Avatar,
  Breadcrumb,
  message,
  Typography,
  Divider,
  Form,
  Input,
  Button,
  Upload,
  Switch,
  Badge,
  Card,
  theme,
  ColorPicker,
  Popover,
  Tabs,
  List,
  Tooltip,
  Alert,
  Slider,
  Drawer
} from 'antd';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import routes from '../../routes';
import axiosInstance from '../../axiosInstance';
import Pusher from 'pusher-js';

import { BACKENDURL, BACKENDURLFiles, getUserData } from '../constants';
import { useGlobalState } from '../../GlobalStateContext';
import { Tag, Radio } from 'antd';

import '../Dashboard.css';

const { Header, Content, Footer, Sider } = Layout;
const { Text, Title } = Typography;
const { useToken } = theme;
const { TabPane } = Tabs;



const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileCollapsed, setMobileCollapsed] = useState(true);
  const [user, setUser] = useState(null);
  const [shop, setShop] = useState(null);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const [pictureForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const { shopId, setShopId } = useGlobalState();
  const { token } = useToken();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  // Theme state
  const [darkTheme, setDarkTheme] = useState(localStorage.getItem('darkTheme') === 'true');
  const [siderTheme, setSiderTheme] = useState(localStorage.getItem('siderTheme') || 'dark');
  const [fixedHeader, setFixedHeader] = useState(localStorage.getItem('fixedHeader') === 'true');
  const [fixedSider, setFixedSider] = useState(localStorage.getItem('fixedSider') === 'true');

  // Custom colors
  const [headerBgColor, setHeaderBgColor] = useState(localStorage.getItem('headerBgColor') || token.colorBgContainer);
  const [headerTextColor, setHeaderTextColor] = useState(localStorage.getItem('headerTextColor') || token.colorText);
  const [siderBgColor, setSiderBgColor] = useState(localStorage.getItem('siderBgColor') || (siderTheme === 'dark' ? '#001529' : '#fff'));
  const [siderTextColor, setSiderTextColor] = useState(localStorage.getItem('siderTextColor') || (siderTheme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)'));
  const [siderIconColor, setSiderIconColor] = useState(localStorage.getItem('siderIconColor') || (siderTheme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)'));
  const [contentBgColor, setContentBgColor] = useState(localStorage.getItem('contentBgColor') || '');
  const [contentTextColor, setContentTextColor] = useState(localStorage.getItem('contentTextColor') || token.colorText);
  const [activeMenuItemColor, setActiveMenuItemColor] = useState(localStorage.getItem('activeMenuItemColor') || '#1890ff');
  const [modalTextColor, setModalTextColor] = useState(localStorage.getItem('modalTextColor') || token.colorText);

  // Branding customization
  const [customLogo, setCustomLogo] = useState(localStorage.getItem('customLogo') || '/images/logo2.png');
  const [systemName, setSystemName] = useState(localStorage.getItem('systemName') || '');
  const [siderTransparency, setSiderTransparency] = useState(localStorage.getItem('siderTransparency') || '0');

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [pusherClient, setPusherClient] = useState(null);

  // Pusher configuration
  const PUSHER_CONFIG = {
    key: '101b707300ca439b512c',
    cluster: 'mt1',
    encrypted: true
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setMobileCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clearAllNotifications = async (shopId) => {
    try {
      await axiosInstance.post(`/notifications/${shopId}/mark-all-read`);
      await axiosInstance.delete(`/notifications/${shopId}/clear`);
      setNotifications([]);
      setUnreadCount(0);
      message.success('All notifications cleared successfully');
    } catch (error) {
      message.error('Failed to clear notifications');
    }
  };

  const getMenuItems = (routes, textColor, iconColor, location) =>
    routes
      .filter((route) => route.key !== 'hide')
      .map((route) => {
        const isActive = location.pathname === route.path;

        return {
          key: route.key,
          icon: route.icon ? React.cloneElement(route.icon, {
            style: { color: isActive ? activeMenuItemColor : iconColor }
          }) : null,
          label: route.path ? (
            <Link to={route.path} style={{ color: isActive ? activeMenuItemColor : textColor }}>
              {route.label}
            </Link>
          ) : (
            <span style={{ color: isActive ? activeMenuItemColor : textColor }}>{route.label}</span>
          ),
          children: route.children ? getMenuItems(route.children, textColor, iconColor, location) : undefined,
          style: isActive ? {
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            borderRight: `3px solid ${activeMenuItemColor}`,
          } : {},
        };
      });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const fetchNotifications = async (shopId) => {
    try {
      setLoadingNotifications(true);
      const response = await axiosInstance.get(`/notifications/${shopId}`);
      const formattedNotifications = response.data.map(notification => ({
        id: notification.id,
        title: notification.title,
        description: notification.message,
        time: formatTime(notification.created_at),
        read: notification.pivot?.is_read || false,
        createdAt: notification.created_at
      }));

      setNotifications(formattedNotifications);
      setUnreadCount(formattedNotifications.filter(n => !n.read).length);
    } catch (error) {
      message.error('Failed to fetch notifications');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axiosInstance.post(`/notifications/${id}/mark-read`);
      setNotifications(prev => prev.map(n =>
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => prev - 1);
    } catch (error) {
      message.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async (shopId) => {
    try {
      await axiosInstance.post(`/notifications/${shopId}/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      message.error('Failed to mark all notifications as read');
    }
  };

  const initializePusher = (userId) => {
    const pusher = new Pusher(PUSHER_CONFIG.key, {
      cluster: PUSHER_CONFIG.cluster,
      encrypted: PUSHER_CONFIG.encrypted,
      authEndpoint: `${BACKENDURL}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    });

    const channel = pusher.subscribe(`private-user.${userId}`);

    channel.bind('App\\Events\\NewNotification', (data) => {
      const newNotification = {
        id: data.notification.id,
        title: data.notification.title,
        description: data.notification.message,
        time: 'Just now',
        read: false,
        createdAt: new Date().toISOString()
      };

      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      message.info(`New notification: ${data.notification.title}`);
    });

    setPusherClient(pusher);
    return pusher;
  };

  useEffect(() => {
    const userData = getUserData();
    if (userData) {
      setUser(userData.user);
      const userShops = userData.user.shops || [];
      const storedShopId = localStorage.getItem('shopId');
      const selectedShop = userShops.find(
        (shop) => shop.id === parseInt(storedShopId)
      );
      if (selectedShop) {
        setShop(selectedShop);
        fetchNotifications(selectedShop.id);
      } else if (userShops.length > 0) {
        setShop(userShops[0]);
        localStorage.setItem('shopId', userShops[0].id);
        fetchNotifications(userShops[0].id);
      }

      const pusher = initializePusher(userData.user.id);

      return () => {
        if (pusher) {
          pusher.disconnect();
        }
      };
    }

    document.body.classList.toggle('dark-theme', darkTheme);
    document.documentElement.style.setProperty('--active-menu-item-color', activeMenuItemColor);
  }, [darkTheme, activeMenuItemColor]);

  useEffect(() => {
    if (shop) {
      fetchNotifications(shop.id);
    }
  }, [shop]);

  const handleLogout = () => {
    if (pusherClient) {
      pusherClient.disconnect();
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    axiosInstance
      .post('logout')
      .then(() => navigate('/login'))
      .catch(() => navigate('/login'));
  };

  const handleShopSelect = (selectedShop) => {
    setShop(selectedShop);
    localStorage.setItem('shopId', selectedShop.id);
    message.success(`Default shop set to: ${selectedShop.name}`);
    fetchNotifications(selectedShop.id);
    window.location.reload();
  };

  const showProfileModal = () => {
    setIsProfileModalVisible(true);
  };

  const showChangePasswordModal = () => {
    setIsChangePasswordModalVisible(true);
  };

  const showSettingsModal = () => {
    setIsSettingsModalVisible(true);
  };

  const handleProfileModalClose = () => {
    setIsProfileModalVisible(false);
  };

  const handleChangePasswordModalClose = () => {
    setIsChangePasswordModalVisible(false);
  };

  const handleSettingsModalClose = () => {
    setIsSettingsModalVisible(false);
  };

  const toggleTheme = (checked) => {
    setDarkTheme(checked);
    localStorage.setItem('darkTheme', checked);
    if (checked) {
      setSiderTheme('dark');
      localStorage.setItem('siderTheme', 'dark');
      setSiderBgColor('#001529');
      setSiderTextColor('rgba(255, 255, 255, 0.65)');
      setSiderIconColor('rgba(255, 255, 255, 0.65)');
    }
  };

  const handleSiderThemeChange = (value) => {
    setSiderTheme(value);
    localStorage.setItem('siderTheme', value);
    if (value === 'dark') {
      setSiderBgColor('#001529');
      setSiderTextColor('rgba(255, 255, 255, 0.65)');
      setSiderIconColor('rgba(255, 255, 255, 0.65)');
    } else {
      setSiderBgColor('#fff');
      setSiderTextColor('rgba(0, 0, 0, 0.88)');
      setSiderIconColor('rgba(0, 0, 0, 0.88)');
    }
  };

  const handleFixedHeaderChange = (checked) => {
    setFixedHeader(checked);
    localStorage.setItem('fixedHeader', checked);
  };

  const handleFixedSiderChange = (checked) => {
    setFixedSider(checked);
    localStorage.setItem('fixedSider', checked);
  };

  const handleTransparencyChange = (value) => {
    setSiderTransparency(value);
    localStorage.setItem('siderTransparency', value);
  };

  const handleColorChange = (color, colorType) => {
    const hexColor = color.toHexString();
    switch (colorType) {
      case 'headerBg':
        setHeaderBgColor(hexColor);
        localStorage.setItem('headerBgColor', hexColor);
        break;
      case 'headerText':
        setHeaderTextColor(hexColor);
        localStorage.setItem('headerTextColor', hexColor);
        break;
      case 'siderBg':
        setSiderBgColor(hexColor);
        localStorage.setItem('siderBgColor', hexColor);
        break;
      case 'siderText':
        setSiderTextColor(hexColor);
        localStorage.setItem('siderTextColor', hexColor);
        break;
      case 'siderIcon':
        setSiderIconColor(hexColor);
        localStorage.setItem('siderIconColor', hexColor);
        break;
      case 'contentBg':
        setContentBgColor(hexColor ?? '');
        localStorage.setItem('contentBgColor', hexColor);
        break;
      case 'contentText':
        setContentTextColor(hexColor);
        localStorage.setItem('contentTextColor', hexColor);
        break;
      case 'activeMenuItem':
        setActiveMenuItemColor(hexColor);
        localStorage.setItem('activeMenuItemColor', hexColor);
        document.documentElement.style.setProperty('--active-menu-item-color', hexColor);
        break;
      case 'modalText':
        setModalTextColor(hexColor);
        localStorage.setItem('modalTextColor', hexColor);
        break;
      default:
        break;
    }
  };

  const handleLogoUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const logoUrl = e.target.result;
      setCustomLogo(logoUrl);
      localStorage.setItem('customLogo', logoUrl);
      message.success('Logo updated successfully');
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleSystemNameChange = (e) => {
    const name = e.target.value;
    setSystemName(name);
    localStorage.setItem('systemName', name);
  };

  const resetColors = () => {
    const defaultColors = {
      headerBg: token.colorBgContainer,
      headerText: token.colorText,
      siderBg: siderTheme === 'dark' ? '#001529' : '#fff',
      siderText: siderTheme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)',
      siderIcon: siderTheme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)',
      contentBg: '',
      contentText: token.colorText,
      activeMenuItem: '#1890ff',
      modalText: token.colorText
    };

    setHeaderBgColor(defaultColors.headerBg);
    setHeaderTextColor(defaultColors.headerText);
    setSiderBgColor(defaultColors.siderBg);
    setSiderTextColor(defaultColors.siderText);
    setSiderIconColor(defaultColors.siderIcon);
    setContentBgColor(defaultColors.contentBg ?? '');
    setContentTextColor(defaultColors.contentText);
    setActiveMenuItemColor(defaultColors.activeMenuItem);
    setModalTextColor(defaultColors.modalText);

    localStorage.removeItem('headerBgColor');
    localStorage.removeItem('headerTextColor');
    localStorage.removeItem('siderBgColor');
    localStorage.removeItem('siderTextColor');
    localStorage.removeItem('siderIconColor');
    localStorage.removeItem('contentBgColor');
    localStorage.removeItem('contentTextColor');
    localStorage.removeItem('activeMenuItemColor');
    localStorage.removeItem('modalTextColor');

    document.documentElement.style.setProperty('--active-menu-item-color', defaultColors.activeMenuItem);
  };

  const resetBranding = () => {
    setCustomLogo('/images/logo2.png');
    setSystemName('');
    localStorage.removeItem('customLogo');
    localStorage.removeItem('systemName');
  };

  const profileMenu = (
    <Menu>
      <Menu.Item key="1" icon={<UserOutlined />} onClick={showProfileModal}>
        Profile
      </Menu.Item>
      <Menu.Item key="2" icon={<SettingOutlined />} onClick={showChangePasswordModal}>
        Change Password
      </Menu.Item>
      <Menu.Item key="3" icon={<LogoutOutlined />} onClick={handleLogout}>
        Log Out
      </Menu.Item>
    </Menu>
  );

  const shopMenu = (
    <Menu style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <Menu.ItemGroup title="Your Shops">
        {user?.shops?.map((shopItem) => (
          <Menu.Item
            key={shopItem.id}
            onClick={() => handleShopSelect(shopItem)}
            style={{
              backgroundColor: shop?.id === shopItem.id ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
              borderLeft: shop?.id === shopItem.id ? `3px solid ${activeMenuItemColor}` : 'none'
            }}
          >
            <Space>
              <ShopOutlined />
              <span>{shopItem?.name}</span>
            </Space>
          </Menu.Item>
        ))}
      </Menu.ItemGroup>
    </Menu>
  );

  const notificationsMenu = (
    <Menu
      style={{
        width: '100%',
        maxWidth: 350,
        padding: 0,
        maxHeight: '80vh',
        position: 'relative',
        left: 0,
        right: 0
      }}
    >
      <Menu.ItemGroup
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Notifications</span>
            <Space>
              {notifications.length > 0 && (
                <>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAllAsRead(shop?.id);
                    }}
                    loading={loadingNotifications}
                  >
                    Mark all as read
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllNotifications(shop?.id);
                    }}
                    loading={loadingNotifications}
                  >
                    Clear all
                  </Button>
                </>
              )}
            </Space>
          </div>
        }
      >
        <div style={{
          maxHeight: 'calc(80vh - 120px)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
        }}>
          {loadingNotifications ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Text type="secondary">Loading notifications...</Text>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Text type="secondary">No notifications yet</Text>
            </div>
          ) : (
            notifications.map(notification => (
              <Menu.Item
                key={notification.id}
                style={{
                  backgroundColor: !notification.read ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
                  borderLeft: !notification.read ? `3px solid ${activeMenuItemColor}` : 'none',
                  padding: '12px 16px',
                  whiteSpace: 'normal'
                }}
              >
                <List.Item
                  style={{ padding: 0 }}
                  onClick={() => markAsRead(notification.id)}
                >
                  <List.Item.Meta
                    title={<Text strong>{notification.title}</Text>}
                    description={notification.description}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>{notification.time}</Text>
                </List.Item>
              </Menu.Item>
            ))
          )}
        </div>
      </Menu.ItemGroup>
      {notifications.length > 0 && (
        <>
          <Menu.Divider />
          <Menu.Item key="view-all" style={{ textAlign: 'center' }}>
            <Button type="link">View All Notifications</Button>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const handleProfilePictureChange = async (file) => {
    const formData = new FormData();
    formData.append('profile_pic', file);
    setLoading(true);
    try {
      const response = await axiosInstance.post('/profile/upload-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success(response.data.message);
      setUser({ ...user, profile_pic: response.data.profile_pic_url });
      pictureForm.resetFields();
    } catch (error) {
      message.error('Failed to upload profile picture');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleChangePassword = async (values) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/profile/change-password', values);
      message.success(response.data.message);
      passwordForm.resetFields();
      setIsChangePasswordModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = location.pathname
    .split('/')
    .filter((path) => path)
    .map((path, index, arr) => {
      const url = `/${arr.slice(0, index + 1).join('/')}`;
      return (
        <Breadcrumb.Item key={url}>
          <Link to={url}>{path.replace(/-/g, ' ')}</Link>
        </Breadcrumb.Item>
      );
    });

  const renderDesktopSider = () => (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      breakpoint="lg"
      collapsedWidth={80}
      theme={siderTheme}
      width={250}
      className={`dashboard-sider ${fixedSider ? 'fixed-sider' : ''}`}
      style={{
        overflow: 'auto',
        minHeight: '100vh',
        position: fixedSider ? 'fixed' : 'relative',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 10,
        background: siderTransparency === '100' ? 'transparent' : siderBgColor,
        borderRight: siderTransparency === '100' ? 'none' : `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`,
        opacity: 1 - (parseInt(siderTransparency) / 100)
      }}
    >
      <div className="logo-container" style={{ padding: collapsed ? '16px 8px' : '16px 40px' }}>
        <Link to="/">
          {<img
            src={customLogo}
            alt="Logo"
            className="logo-img"
            style={{ width: collapsed ? '32px' : '100%' }}
          />}
          {!collapsed && systemName && (
            <div style={{
              color: siderTextColor,
              textAlign: 'center',
              marginTop: 8,
              fontWeight: 'bold',
              fontSize: 18
            }}>
              {systemName}
            </div>
          )}
        </Link>
      </div>

      <div className="sider-content">
        <Menu
          theme={siderTheme}
          mode="inline"
          selectedKeys={[location.pathname]}
          items={getMenuItems(routes, siderTextColor, siderIconColor, location)}
          className="dashboard-menu"
          style={{
            background: siderTransparency === '100' ? 'transparent' : siderBgColor,
            borderRight: 'none'
          }}
        />

        {!collapsed && (
          <div className="theme-switch-container" style={{
            padding: '16px',
            borderTop: siderTransparency === '100' ? 'none' : `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`,
            background: siderTransparency === '100' ? 'transparent' : siderBgColor
          }}>
            <Switch
              checkedChildren={<BulbFilled />}
              unCheckedChildren={<BulbOutlined />}
              checked={darkTheme}
              onChange={toggleTheme}
              style={{ marginRight: 8 }}
            />
            <span className="theme-label" style={{ color: siderTextColor }}>
              {darkTheme ? 'Dark' : 'Light'} Theme
            </span>
          </div>
        )}
      </div>
    </Sider>
  );

  const renderMobileDrawer = () => (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={customLogo}
            alt="Logo"
            style={{ width: 32, marginRight: 8 }}
          />
          {systemName}
        </div>
      }
      placement="left"
      closable={true}
      onClose={() => setMobileCollapsed(true)}
      open={!mobileCollapsed}
      bodyStyle={{
        padding: 0,
        background: siderTransparency === '100' ? 'transparent' : siderBgColor,
        color: siderTextColor
      }}
      headerStyle={{
        background: siderBgColor,
        color: siderTextColor,
        borderBottom: `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`
      }}
      width={250}
    >
      <div className="sider-content">
        <Menu
          theme={siderTheme}
          mode="inline"
          selectedKeys={[location.pathname]}
          items={getMenuItems(routes, siderTextColor, siderIconColor, location)}
          className="dashboard-menu"
          style={{
            background: siderTransparency === '100' ? 'transparent' : siderBgColor,
            borderRight: 'none'
          }}
          onClick={() => setMobileCollapsed(true)}
        />

        <div className="theme-switch-container" style={{
          padding: '16px',
          borderTop: siderTransparency === '100' ? 'none' : `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`,
          background: siderTransparency === '100' ? 'transparent' : siderBgColor
        }}>
          <Switch
            checkedChildren={<BulbFilled />}
            unCheckedChildren={<BulbOutlined />}
            checked={darkTheme}
            onChange={toggleTheme}
            style={{ marginRight: 8 }}
          />
          <span className="theme-label" style={{ color: siderTextColor }}>
            {darkTheme ? 'Dark' : 'Light'} Theme
          </span>
        </div>
      </div>
    </Drawer>
  );

  return (
    <Layout
      className={`dashboard-layout ${darkTheme ? 'dark-theme' : ''}`}
      style={{
        background: contentBgColor,
        color: contentTextColor,
        minHeight: '100vh'
      }}
    >
      {!isMobile ? renderDesktopSider() : renderMobileDrawer()}

      <Layout style={{ marginLeft: !isMobile && fixedSider ? (collapsed ? 80 : 250) : 0 }}>
        <Header
          className={`dashboard-header ${fixedHeader ? 'fixed-header' : ''}`}
          style={{
            padding: '0 24px',
            background: headerBgColor,
            color: headerTextColor,
            boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
            position: fixedHeader ? 'fixed' : 'static',
            top: 0,
            right: 0,
            left: !isMobile && fixedSider ? (collapsed ? 80 : 250) : 0,
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all 0.2s',
          }}
        >
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: headerTextColor }} />}
              onClick={() => setMobileCollapsed(false)}
              style={{ marginRight: 16 }}
            />
          )}

          <Breadcrumb className="dashboard-breadcrumb" style={{ color: headerTextColor }}>
            {breadcrumbItems}
          </Breadcrumb>

          <div className="header-right">
            <Space size="middle">
              <Dropdown
                overlay={notificationsMenu}
                trigger={['click']}
                placement="bottomRight"
                overlayStyle={{ zIndex: 1050 }}
              >
                <Badge count={unreadCount} offset={[-5, 5]}>
                  <Button
                    type="text"
                    icon={<BellOutlined style={{ color: headerTextColor, fontSize: 18 }} />}
                    style={{ color: headerTextColor }}
                  />
                </Badge>
              </Dropdown>

              <Tooltip title="Settings">
                <Button
                  type="text"
                  icon={<SettingOutlined style={{ color: headerTextColor, fontSize: 18 }} />}
                  onClick={showSettingsModal}
                  className="settings-btn"
                  style={{ color: headerTextColor }}
                />
              </Tooltip>

              {shop && (
                <Dropdown
                  overlay={shopMenu}
                  trigger={['click']}
                  overlayStyle={{ zIndex: 1050 }}
                >
                  <Badge count={user?.shops?.length || 0}>
                    <Button
                      type="text"
                      icon={<ShopOutlined style={{ color: headerTextColor, fontSize: 18 }} />}
                      style={{ color: headerTextColor }}
                    >
                      {!isMobile && !collapsed && <span>{shop?.name}</span>}
                      <DownOutlined style={{ color: headerTextColor, fontSize: 12 }} />
                    </Button>
                  </Badge>
                </Dropdown>
              )}

              <Dropdown
                overlay={profileMenu}
                trigger={['click']}
                overlayStyle={{ zIndex: 1050 }}
              >
                <Space className="user-dropdown" style={{ cursor: 'pointer', padding: '4px 8px' }}>
                  <Avatar
                    src={user?.profile_pic ? `${BACKENDURLFiles}${user.profile_pic}` : undefined}
                    icon={<UserOutlined />}
                    size="default"
                    style={{ backgroundColor: activeMenuItemColor }}
                  />
                  {!isMobile && !collapsed && (
                    <div className="user-info">
                      <div className="user-name" style={{ color: headerTextColor }}>
                        {user?.name}
                      </div>
                    </div>
                  )}
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>

        <Content
          className="dashboard-content"
          style={{
            marginTop: fixedHeader ? 64 : 0,
            padding: 24,
            minHeight: 'calc(100vh - 112px)',
            background: contentBgColor,
            color: contentTextColor,
          }}
        >
          <div className="dashboard-container">
            <Outlet context={{ contentBgColor, contentTextColor }} />
          </div>
        </Content>

        <Footer className="dashboard-footer" style={{
          textAlign: 'center',
          background: headerBgColor,
          color: headerTextColor,
          padding: '16px 24px'
        }}>
          ©{new Date().getFullYear()} {systemName} - Created by JCODES TECHNOLOGY
        </Footer>
      </Layout>

      {/* Profile Modal */}
      <Modal
        title="User Profile"
        open={isProfileModalVisible}
        onCancel={handleProfileModalClose}
        footer={null}
        width={600}
      >
        <Divider />
        <Form form={pictureForm}>
          <div className="profile-picture-container">
            <Upload
              name="profile_pic"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={false}
              customRequest={({ file }) => handleProfilePictureChange(file)}
            >
              {user?.profile_pic ? (
                <Avatar src={`${BACKENDURLFiles}${user.profile_pic}`} size={100} />
              ) : (
                <Avatar icon={<UserOutlined />} size={100} />
              )}
            </Upload>
            <div className="profile-info">
              <Title level={4} style={{ color: modalTextColor }}>{user?.name}</Title>
              <Text type="secondary" style={{ color: modalTextColor }}>{user?.email}</Text>
            </div>
          </div>

          <Divider orientation="left">Personal Information</Divider>
          <div className="profile-details">
            <div className="detail-item">
              <Text strong style={{ color: modalTextColor }}>Role:</Text>
              <Text style={{ color: modalTextColor }}>{user?.role?.name}</Text>
            </div>
            <div className="detail-item">
              <Text strong style={{ color: modalTextColor }}>Phone:</Text>
              <Text style={{ color: modalTextColor }}>{user?.phone || 'Not provided'}</Text>
            </div>
            <div className="detail-item">
              <Text strong style={{ color: modalTextColor }}>Retailer Number:</Text>
              <Text style={{ color: modalTextColor }}>{user?.retailer_number || 'Not provided'}</Text>
            </div>
            <div className="detail-item">
              <Text strong style={{ color: modalTextColor }}>Shops:</Text>
              <div className="shops-list">
                {user?.shops?.map((shopItem) => (
                  <Tag key={shopItem.id} color="blue">{shopItem?.name}</Tag>
                ))}
              </div>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={isChangePasswordModalVisible}
        onCancel={handleChangePasswordModalClose}
        footer={null}
      >
        <Form form={passwordForm} onFinish={handleChangePassword} layout="vertical">
          <Form.Item
            name="current_password"
            label={<span style={{ color: modalTextColor }}>Current Password</span>}
            rules={[{ required: true, message: 'Please enter your current password!' }]}
          >
            <Input.Password size="large" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label={<span style={{ color: modalTextColor }}>New Password</span>}
            rules={[
              { required: true, message: 'Please enter your new password!' },
              { min: 8, message: 'Password must be at least 8 characters!' }
            ]}
          >
            <Input.Password size="large" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label={<span style={{ color: modalTextColor }}>Confirm Password</span>}
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject('Passwords do not match!');
                },
              }),
            ]}
          >
            <Input.Password size="large" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<CheckOutlined />}
              >
                Change Password
              </Button>
              <Button
                onClick={handleChangePasswordModalClose}
                icon={<CloseOutlined />}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Settings Modal */}
      <Modal
        title="Dashboard Settings"
        open={isSettingsModalVisible}
        onCancel={handleSettingsModalClose}
        footer={null}
        width={800}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Theme" key="1">
            <Form form={settingsForm} layout="vertical">
              <Form.Item label={<span style={{ color: modalTextColor }}>Theme Settings</span>}>
                <Card size="small">
                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>Dark Theme</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Switch between light and dark themes</Text>
                    </div>
                    <Switch
                      checkedChildren={<BulbFilled />}
                      unCheckedChildren={<BulbOutlined />}
                      checked={darkTheme}
                      onChange={toggleTheme}
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>Sider Theme</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Change sidebar color scheme</Text>
                    </div>
                    <Radio.Group
                      value={siderTheme}
                      onChange={(e) => handleSiderThemeChange(e.target.value)}
                    >
                      <Radio.Button value="light">Light</Radio.Button>
                      <Radio.Button value="dark">Dark</Radio.Button>
                    </Radio.Group>
                  </div>
                </Card>
              </Form.Item>

              <Form.Item label={<span style={{ color: modalTextColor }}>Color Customization</span>}>
                <Card size="small">
                  <Alert
                    message="Customize the look and feel of your dashboard"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <BgColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Header Background</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={headerBgColor}
                      onChange={(color) => handleColorChange(color, 'headerBg')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <FontColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Header Text</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={headerTextColor}
                      onChange={(color) => handleColorChange(color, 'headerText')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <BgColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Sider Background</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={siderBgColor}
                      onChange={(color) => handleColorChange(color, 'siderBg')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <FontColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Sider Text</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={siderTextColor}
                      onChange={(color) => handleColorChange(color, 'siderText')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <FontColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Sider Icons</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={siderIconColor}
                      onChange={(color) => handleColorChange(color, 'siderIcon')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <BgColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Content Background</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={contentBgColor}
                      onChange={(color) => handleColorChange(color, 'contentBg')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <FontColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Content Text</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={contentTextColor}
                      onChange={(color) => handleColorChange(color, 'contentText')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <FontColorsOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Modal Text</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={modalTextColor}
                      onChange={(color) => handleColorChange(color, 'modalText')}
                      showText
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Space>
                        <HighlightOutlined style={{ color: modalTextColor }} />
                        <Text strong style={{ color: modalTextColor }}>Active Menu Item</Text>
                      </Space>
                    </div>
                    <ColorPicker
                      value={activeMenuItemColor}
                      onChange={(color) => handleColorChange(color, 'activeMenuItem')}
                      showText
                    />
                  </div>

                  <Divider />

                  <Button
                    type="default"
                    onClick={resetColors}
                    style={{ width: '100%' }}
                    icon={<HighlightOutlined />}
                  >
                    Reset to Default Colors
                  </Button>
                </Card>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Layout" key="2">
            <Form form={settingsForm} layout="vertical">
              <Form.Item label={<span style={{ color: modalTextColor }}>Layout Settings</span>}>
                <Card size="small">
                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>Fixed Header</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Keep header fixed on top</Text>
                    </div>
                    <Switch
                      checked={fixedHeader}
                      onChange={handleFixedHeaderChange}
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>Fixed Sider</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Keep sidebar fixed on left</Text>
                    </div>
                    <Switch
                      checked={fixedSider}
                      onChange={handleFixedSiderChange}
                    />
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>Sider Transparency</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Adjust sidebar transparency</Text>
                    </div>
                    <Slider
                      min={0}
                      max={100}
                      value={parseInt(siderTransparency)}
                      onChange={handleTransparencyChange}
                      style={{ width: 150 }}
                    />
                  </div>
                </Card>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Branding" key="3">
            <Form form={settingsForm} layout="vertical">
              <Form.Item label={<span style={{ color: modalTextColor }}>Branding Customization</span>}>
                <Card size="small">
                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>System Logo</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Upload your custom logo</Text>
                    </div>
                    <Upload
                      name="logo"
                      listType="picture-card"
                      className="logo-uploader"
                      showUploadList={false}
                      beforeUpload={handleLogoUpload}
                    >
                      {customLogo ? (
                        <img src={customLogo} alt="Logo" style={{ width: '100%' }} />
                      ) : (
                        <div>
                          <PictureOutlined style={{ fontSize: 24 }} />
                          <div style={{ marginTop: 8 }}>Upload Logo</div>
                        </div>
                      )}
                    </Upload>
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <Text strong style={{ color: modalTextColor }}>System Name</Text>
                      <Text type="secondary" style={{ color: modalTextColor }}>Change the system display name</Text>
                    </div>
                    <Input
                      value={systemName}
                      onChange={handleSystemNameChange}
                      placeholder="Enter system name"
                      style={{ width: 200 }}
                    />
                  </div>

                  <Divider />

                  <Button
                    type="default"
                    onClick={resetBranding}
                    style={{ width: '100%' }}
                    icon={<HighlightOutlined />}
                  >
                    Reset to Default Branding
                  </Button>
                </Card>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>
    </Layout>
  );
};

export default Dashboard;